import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import parse from 'html-react-parser';

import ApolloWrapper from '../../../pages/LandingPage/ApolloWrapper';
import { useQuery } from '@apollo/client';
import { Map }      from 'immutable';

import Button         from '@mui/material/Button';
import Link           from '@mui/material/Link';
import Grid           from '@mui/material/Grid';
import Box            from '@mui/material/Box';
import Container      from '@mui/material/Container';
import Typography     from '@mui/material/Typography';
import CheckCircle    from '@mui/icons-material/CheckCircle';

import { Iterable } from 'immutable';

import { isValidURL } from 'helpers/url';
import applicationJourneyGtm from 'helpers/gtm';

import recordJS      from 'records';
import { CANDIDATE_APPLICATION_COMPLETION_QUERY_JLP } from 'queries/candidate/applicant';

import Display from 'components/Generic/Display';

import './Completion.scss'

// TODO: navigate to questions scren if there are still some unanswered questions
function Completion({
  theme, message, redirectUrl, refreshRate, ctas, campaign,
}) {
  const [state, setState] = useState(new Map());

  const text       = message && message.length > 0 ? message : t('eos.landing_page.text.completion_message', { job: campaign.title });
  const activeCtas = (ctas instanceof Array) ? ctas.filter((c) => c.active) : [];

  const done = state.get('done', false);
  const refetched = state.get('refetched', false);
  const reloading = state.get('reloading', false);

  const {
    loading, error, data, refetch,
  } = useQuery(CANDIDATE_APPLICATION_COMPLETION_QUERY_JLP, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (!data) return;

    const record = recordJS({ data });
    const applicant = record.getIn(['data', 'candidate', 'applications', 'collection', 0]);

    applicationJourneyGtm(campaign.id).thanksView(applicant.id);

    // TEMP solution to replace color with company's primary one.
    const callToActionCompletionMessage = applicant.callToActionCompletionMessage.replaceAll('class="cta-link-button"', `class="cta-link-button" style="background-color: ${theme.palette.primary.main};"`);

    if (callToActionCompletionMessage) {
      setState(state.set('refetched', true)
        .set('reloading', false)
        .set('callToActionCompletionMessage', callToActionCompletionMessage));
    }

    handleRedirect();
  }, [data]);

  function handleRedirect() {
    if (!redirectUrl || !isValidURL(redirectUrl)) return;

    const parsedDelay = Number(refreshRate);

    // NOTE: Setting a minimum of 2 seconds delay to avoid immediate transition
    // from completion screen to redirect url.
    const delaySeconds = parsedDelay > 1 ? parsedDelay : 2;

    setTimeout(() => {
      window.location.href = redirectUrl;
    }, delaySeconds * 1000);
  }

  if (loading || error || !data) return null;

  function isValidCompletionMessage() {
    const completionMessage = state.get('callToActionCompletionMessage');
    if (completionMessage === null || completionMessage === "" || completionMessage === "<p></p>") {
      return false;
    }
    return true;
  }

  return (
    <Container>
      <Grid container sx={{ mt: 5, mb: 6, justifyContent: 'center' }}>
        <Grid
          item
          md={6}
          sm={10}
          xs={12}
          sx={{
            display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center'
          }}
        >
          <CheckCircle
            color="primary"
            sx={{
              fontSize:                       60,
              [theme.breakpoints.only('xs')]: {
                fontSize: 50,
              },
            }}
          />

          {
            state.get('callToActionCompletionMessage') ? (
              <Box sx={{ mt: 3, width: '100%', textAlign: 'center' }} data-testid="completion-text">
                {parse(state.get('callToActionCompletionMessage'))}
              </Box>
            ) : (
              <Typography variant="h5" sx={{ mt: 3, textAlign: 'left' }} data-testid="completion-text">
                {text}
              </Typography>
            )
          }
        </Grid>
      </Grid>
      <Display if={!isValidCompletionMessage()} >
        <Grid container sx={{ mt: 2, justifyContent: 'center' }}>
          <Grid
            item
            md={3}
            sm={4}
            xs={10}
            sx={{
              display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
            }}
          >
            {
              activeCtas.map((cta) => (
                <Link key={cta.label} href={cta.url} target="_blank" rel="noopener" variant="body" underline="none" sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                  <Button variant="contained" disableElevation color="primary" size="large" sx={{ width: '100%', textTransform: 'inherit', my: 1 }}>
                    {cta.label}
                  </Button>
                </Link>
              ))
            }
          </Grid>
        </Grid>
      </Display>
    </Container>
  );
}

function CompletionApollo({
  token, campaign, theme, message, redirectUrl, refreshRate, ctas,
}) {
  return (
    <ApolloWrapper token={token}>
      <Completion
        campaign={campaign}
        theme={theme}
        message={message}
        redirectUrl={redirectUrl}
        refreshRate={refreshRate}
        ctas={ctas}
      />
    </ApolloWrapper>
  );
}

Completion.defaultProps = {
  campaign:                      null,
  theme:                         {},
  message:                       null,
  redirectUrl:                   null,
  refreshRate:                   null,
  ctas:                          [],
  callToActionCompletionMessage: null,
};

Completion.propTypes = {
  campaign:                      PropTypes.object,
  theme:                         PropTypes.objectOf(Iterable),
  message:                       PropTypes.string,
  redirectUrl:                   PropTypes.string,
  refreshRate:                   PropTypes.string,
  ctas:                          PropTypes.arrayOf(Iterable),
  callToActionCompletionMessage: PropTypes.string,
};

CompletionApollo.defaultProps = {
  token:                         undefined,
  campaign:                      null,
  theme:                         {},
  message:                       null,
  redirectUrl:                   null,
  refreshRate:                   null,
  ctas:                          [],
  callToActionCompletionMessage: null,
};

CompletionApollo.propTypes = {
  token:                         PropTypes.string,
  campaign:                      PropTypes.object,
  theme:                         PropTypes.objectOf(Iterable),
  message:                       PropTypes.string,
  redirectUrl:                   PropTypes.string,
  refreshRate:                   PropTypes.string,
  ctas:                          PropTypes.arrayOf(Iterable),
  callToActionCompletionMessage: PropTypes.string,
};

export default CompletionApollo;
